import { useState, useEffect } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import isEqual from 'lodash/isEqual';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices, { CandidatesResponse } from '@/services/candidates';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds } from '@/types';
import TalentPoolListComponent from './TalentPoolList.component';
import { DEFAULT_PAGINATION_DATA, DEFAULT_SORT_BY } from './TalentPoolList.constants';
import {
  getPaginationDataFromLocalStorage,
  setPaginationDataToLocalStorage,
} from './TalentPoolList.utils';
import { PaginationData } from '../JobsList/JobsList.utils';

const TALENT_POOL_FILTER_OBJECT = {
  field: 'isTalentPool',
  condition: 'is',
  value: 'Yes',
};

const TALENT_POOL_FILTER = encodeURIComponent(JSON.stringify([TALENT_POOL_FILTER_OBJECT]));

const TalentPoolListPage = function (): JSX.Element {
  const [pagination, setPagination] = useState<PaginationData>(DEFAULT_PAGINATION_DATA);

  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);

  const onUpdateSortBy = (event): void => {
    const {
      target: { value, name },
    } = event;
    setSortBy((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const paginationData = getPaginationDataFromLocalStorage();

    if (paginationData) {
      setPagination(paginationData);
    }
  }, []);

  useEffect(() => {
    const isDefaultSortBy = isEqual(sortBy, DEFAULT_SORT_BY);

    if (!isDefaultSortBy) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  }, [sortBy]);

  useEffect(() => {
    const isDefaultData = isEqual(pagination, DEFAULT_PAGINATION_DATA);

    if (!isDefaultData) {
      setPaginationDataToLocalStorage(pagination);
    }
  }, [pagination]);

  const candidatesResponse = useApiFetch<CandidatesResponse>(
    [
      FetchApiIds.CANDIDATES_FILTERED,
      sortBy.order,
      sortBy.column,
      pagination.currentPage,
      pagination.itemsPerPage,
      TALENT_POOL_FILTER,
    ],
    async () =>
      candidatesServices.getFilteredCandidates({
        take: pagination.itemsPerPage,
        page: pagination.currentPage,
        column: sortBy.column,
        order: sortBy.order,
        filters: TALENT_POOL_FILTER,
      }),
    {
      retry: 1,
    }
  );

  useEffect(() => {
    if (candidatesResponse.data?.currentPage) {
      setPagination((prev) => ({
        ...prev,
        count: candidatesResponse.data?.count,
        currentPage: candidatesResponse.data?.currentPage,
        lastPage: candidatesResponse.data.lastPage,
      }));
    }
  }, [
    candidatesResponse.data?.count,
    candidatesResponse.data?.currentPage,
    candidatesResponse.data?.lastPage,
  ]);

  const onChangePageHandler = (currentPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage,
    }));
  };

  const onChangeRecordsPerPage = (itemsPerPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage,
    }));
  };

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <TalentPoolListComponent
        {...pagination}
        {...sortBy}
        onUpdateSortBy={onUpdateSortBy}
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        isLoading={candidatesResponse.isLoading}
        onChangePageHandler={onChangePageHandler}
        candidatesList={candidatesResponse.data?.data ?? []}
      />
    </ChakraProvider>
  );
};

export default TalentPoolListPage;
